import { concat, fn } from '@ember/helper';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import { restartableTask } from 'ember-concurrency';
import { or } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import FormDetails from './form-details.gts';
import Paginator from 'tio-ui/components/paginator';
import safeFormatDate from '../../../../helpers/safe-format-date.ts';
import Table from 'tio-ui/components/table';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import type RouterService from '@ember/routing/router-service';
import type { Store } from '@warp-drive/core';
import { query } from '@warp-drive/utilities/json-api';
import { LinkTo } from '@ember/routing';
import type Owner from '@ember/owner';
import Button from 'tio-ui/components/button';
import { on } from '@ember/modifier';
import type FeaturesService from '../../../../services/features.ts';

interface PslfAdminProfileEmployeeFormsTableSignature {
  Args: {
    employee: EmployeeModel;
    limit: number;
    isTioAdmin: boolean;
  };
  Element: HTMLTableElement;
}

export default class PslfAdminProfileEmployeeFormsTableComponent extends Component<PslfAdminProfileEmployeeFormsTableSignature> {
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare features: FeaturesService;

  @tracked modelToPreview: PslfFormModel | null = null;
  @tracked page = 1;

  constructor(owner: Owner, args: PslfAdminProfileEmployeeFormsTableSignature['Args']) {
    super(owner, args);
    this.loadRecords.perform();
  }

  get showDialog() {
    return !!this.modelToPreview;
  }

  get formDetailRouteExists() {
    // This will be false within tio-agent
    return !!this.router.recognize('/pslf/forms/id');
  }

  openPreviewModal = async (form: PslfFormModel) => {
    this.modelToPreview = form;
  };

  closePreviewModal = () => {
    this.modelToPreview = null;
  };

  goToFormPage = () => {
    if (this.formDetailRouteExists && this.modelToPreview) {
      this.router.transitionTo('authenticated.pslf.forms.show', this.modelToPreview.id);
    }
  };

  @tracked records?: PslfFormModel[];
  @tracked recordsMeta?: { page?: { total?: number } };

  get isLoading() {
    return this.loadRecords.isRunning;
  }

  loadRecords = restartableTask(async () => {
    const limit = this.args.limit;

    const page = {
      limit,
      offset: (this.page - 1) * limit,
    };
    const filter = {
      // Employee could be an ObjectProxy, therefore must use 'get'
      'employee-id': this.args.employee.id,
      'exclude-created': true,
    };

    // WarpDrive builder read (Phase 2.4/2.6) — replaces store.query;
    // flattened bracket keys satisfy the builder's QueryParamsSource type
    // (identical wire serialization).
    const { content } = (await this.store.request(
      query<PslfFormModel>('pslf-form', {
        'filter[employee-id]': filter['employee-id'] as string,
        'filter[exclude-created]': filter['exclude-created'] as boolean,
        'page[limit]': page.limit,
        'page[offset]': page.offset,
        include: 'pslf-group',
      })
    )) as { content: { data: PslfFormModel[]; meta?: { page?: { total?: number } } } };
    this.records = content.data;
    this.recordsMeta = content.meta;
  });

  changePaginatorPage = (page: number) => {
    this.page = page;
  };

  <template>
    <Table @isLoading={{this.isLoading}} @previewRowLimit={{@limit}} ...attributes>
      <:thead as |options|>
        <options.tr>
          <th class="{{options.cellClass}} w-16"></th>
          <th class={{options.cellClass}}>{{t "common.pslf.date_employer_signed"}}</th>
          <th class={{options.cellClass}}>{{t "common.pslf.date_borrower_signed"}}</th>
          <th class={{options.cellClass}}>{{t "common.status"}}</th>
          <th class={{options.cellClass}}>
            {{t "common.tuition_assistance.program_template_review.approver"}}
          </th>
          <th class={{options.cellClass}}>{{t "common.pslf.forms.form_type"}}</th>
        </options.tr>
      </:thead>
      <:tbody as |options|>
        {{#each this.records as |record|}}
          <options.tr>
            <options.td class={{options.cellClass}}>
              <Button
                @appearance="minimal"
                @size="sm"
                {{on "click" (fn this.openPreviewModal record)}}
              >
                {{t "common.details"}}
              </Button>
            </options.td>
            {{#let record.employerCompletedAt as |date|}}
              <options.td title={{date}}>
                {{#if date}}
                  {{safeFormatDate date month="short" day="2-digit" year="numeric"}}
                {{else if (or record.isPrintAndMail record.isPriorEmployerClosed)}}
                  {{t "pslf.table.not_applicable"}}
                {{else if record.isAwaitingSignature}}
                  {{t "pslf.table.pending"}}
                {{/if}}
              </options.td>
            {{/let}}

            {{#let record.borrowerCompletedAt as |date|}}
              <options.td title={{date}}>
                {{#if date}}
                  {{safeFormatDate date month="short" day="2-digit" year="numeric"}}
                {{else}}
                  {{t "pslf.table.pending"}}
                {{/if}}
              </options.td>
            {{/let}}

            <options.td>{{t (concat "common.pslf.status." record.status)}}</options.td>
            <options.td>{{record.approverDisplayEmail}}</options.td>
            <options.td>
              {{#if record.formType}}
                {{t (concat "common.pslf.form-type." record.formTypeDisplayKey)}}
              {{/if}}
              {{#if record.isPriorEmployer}}
                -
                {{record.employerDisplayName}}
              {{/if}}
            </options.td>
          </options.tr>
        {{else}}
          <options.tr>
            <options.td colspan="6">
              {{t "common.pslf.no_forms_found"}}
            </options.td>
          </options.tr>
        {{/each}}
      </:tbody>
      <:tfoot as |options|>
        <options.tr>
          <td colspan="6">
            <Paginator
              @totalItems={{or this.recordsMeta.page.total 0}}
              @pageSize={{@limit}}
              @currentPage={{this.page}}
              @onChange={{this.changePaginatorPage}}
            />
          </td>
        </options.tr>
        {{#if this.features.isAdminApp}}
          <options.tr>
            <td class="p-4" colspan="6">
              <LinkTo
                @route="authenticated.pslf.profiles.show"
                @model={{@employee.pslfProfile.id}}
                class="underline tio-fg-info"
              >
                {{t "common.pslf.view_pslf_profile"}}
              </LinkTo>
            </td>
          </options.tr>
        {{/if}}
      </:tfoot>
    </Table>
    {{#let this.modelToPreview as |form|}}
      <Modal
        @isOpen={{this.showDialog}}
        @onClose={{this.closePreviewModal}}
        class="min-h-64"
        @size="full"
        @ariaLabel={{t "common.aria_labels.pslf_form_details"}}
      >
        <:header>{{t "common.pslf.document_repo.classification.pslf_form"}}
          {{form.id}}</:header>
        <:body>
          {{#if form}}
            <FormDetails @model={{form}} @isTioAdmin={{@isTioAdmin}} />
          {{/if}}
        </:body>
        <:footer>
          {{#if @isTioAdmin}}
            <Footer
              @isRunning={{false}}
              @onCancel={{this.closePreviewModal}}
              @onSubmit={{this.goToFormPage}}
              @submitText={{t "common.pslf.go_to_form_profile"}}
            />
          {{/if}}
        </:footer>
      </Modal>
    {{/let}}
  </template>
}
